'use client';
import SummaryLine from '@repo/ui/src/components/Cart/SummaryLine';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';
import { Order } from '@repo/ui/src/types/order';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';

export interface OrderSummaryCartProps {
  orderDetails: Order['details'];
}

const OrderDetailCostSummary = ({
  orderDetails: {
    currency,
    orderTotal,
    taxTotal,
    shippingTotal,
    subTotal,
    productItems,
    giftCertificateItems,
  },
}: OrderSummaryCartProps) => {
  const getCurrencyString = useCurrencyString(currency);
  const { t } = useTranslation();
  const totalItems =
    (giftCertificateItems?.length ?? 0) +
    (productItems?.reduce((acc, line) => acc + line.quantity, 0) ?? 0);

  const item = t('orderSummary.item', { count: totalItems }, { default: 'item' });

  return (
    <div className="grid self-stretch grid-rows-[auto_1fr]">
      <h5 className="tra-text-web-heading-5 mb-5">
        {t('orderSummary.orderSummaryTitle', undefined, {
          default: 'Order Summary',
        })}
      </h5>
      <div className="grid content-start gap-y-4 p-6 bg-warm-100 border border-warm-300">
        <div className="gap-y-2 grid">
          <p className="tra-text-web-body-m font-bold text-neutral-400">
            <span>{`${totalItems} ${item} ${getCurrencyString(orderTotal)}`}</span>
          </p>
        </div>
        <hr />
        <div className="grid gap-y-2">
          <SummaryLine
            key="subtotal"
            title={t(
              'orderSummary.subtotalItems',
              {
                totalItems: totalItems,
              },
              { default: `Subtotal ({{totalItems}} items) ` },
            )}
            value={subTotal}
            currency={currency}
          />
          <SummaryLine
            key="key"
            title={t('orderSummary.tax', undefined, {
              default: 'Tax',
            })}
            value={taxTotal}
            currency={currency}
          />
          <SummaryLine
            key="shipping"
            title={t('cart.shipping', undefined, {
              default: 'Shipping',
            })}
            value={shippingTotal}
            currency={currency}
          />
          <hr />
          <SummaryLine
            key="total"
            title={t('orderSummary.orderTotal', undefined, {
              default: 'Order Total',
            })}
            value={orderTotal}
            bold
            currency={currency}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderDetailCostSummary;
